/**
 * Convert Data mapped to vertices of a PolyLine2 to data mapped to edges in an array.
 *
 * VertexData uses the format such that a PolyLine2 `[x,y,x2,y2,x3,y3,x,y]` would have data `[a,a,b,b,c,c,d,d,a,a]`.
 * Each x and y component has a duplicate value.
 *
 * EdgeData would map the same data to edges `[a,b,c,d]`. Additionally, because more data is coming in, the data is stored in an array.
 * This results in `[[a],[b],[c],[d]]`.
 *
 */
export function vertexDataToEdgeDataAsArray<T>(vertexData: T[], edgeData: T[][]) {
  if (!vertexData) {
    return;
  }

  for (let i = 0; i < vertexData.length - 2; i += 2) {
    edgeData[i / 2] = [vertexData[i]];
  }
}

/**
 * See {@link vertexDataToEdgeDataAsArray} for more information. This function is a wrapper for nested arrays.
 */
export function vertexDataArrToEdgeDataAsArrayArr<T>(vertexDataArr: T[][]): T[][][] {
  const edgeDataArr: T[][][] = [];
  for (const vertexData of vertexDataArr) {
    const edgeData: T[][] = [];
    vertexDataToEdgeDataAsArray<T>(vertexData, edgeData);
    edgeDataArr.push(edgeData);
  }

  return edgeDataArr;
}

/**
 * Remap edge data from a PolyLine2 to another where the target polyline is overlapping the source polyline.
 *
 * @param combineData Is the function that determines how data is combined when edges are mapped to the same target index.
 */
export function remapEdgeData<T>(remap: (number | undefined)[], edgeData: T[], combineData: (a: T, b: T) => T) {
  const newEdgeData: T[] = [];
  for (const edgeIndex of Object.keys(remap)) {
    const result = remap[edgeIndex];
    const data = edgeData[edgeIndex];

    if (result === undefined) {
      continue;
    }

    let res = newEdgeData[result];
    if (res) {
      res = combineData(res, data);
    } else {
      res = data;
    }
    newEdgeData[result] = res;
  }
  return newEdgeData;
}
